import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
  Typography,
  IconButton,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  API_GET_ADMIN_MEMEBRS,
  API_GET_UPCOMING_ENQUIRIES,
  API_ADD_UPDATE_TASKS
} from '../../../Util/URLConstants';
import StorageFactory from '../../../Util/StorageFactory';
import { SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import { isEmpty, returnDate } from '../../../Util/UtilHelper';
import MuiTypographyMandatory from '../../../Components/Custom/MuiTypographyMandatory';
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { VM_Colors } from '../../../Styles/VMStyles';
import dayjs from "dayjs";

const TaskDrawer = ({ isOpen, onClose, onCreateTask, reuseTaskData }) => {

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    event: '',
    priority: 'Medium',
    assignedTo: [],
    dueDate: '',
    taskId: null
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);

  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs().endOf("month"));

  const sessionFactory = new StorageFactory("session");
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      event: '',
      priority: 'Medium',
      assignedTo: [],
      dueDate: '',
      taskId: null
    });
    setErrors({});
    setIsSubmitting(false);
    setStartDate(dayjs().startOf("month"));
    setEndDate(dayjs().endOf("month"));
  }, []);

  /* ======================================================
     ⭐ PREFILL WHEN REUSE IS CLICKED
     ====================================================== */
  useEffect(() => {
    if (isOpen && reuseTaskData) {
      setFormData(prev => ({
        ...prev,
        name: reuseTaskData.name || "",
        description: reuseTaskData.description || "",
        event: reuseTaskData.enquiry_info?.appointment_id || "",
        priority: reuseTaskData.priority || "Medium",
        assignedTo: reuseTaskData.assign_users?.map(u => u.user_id) || [],
        dueDate: '', // user selects new one
        taskId: null
      }));
      setErrors({});
    }
  }, [isOpen, reuseTaskData]);

  /* ======================================================
   ⭐ DO NOT RESET THE REUSED DATA
   ONLY reset if drawer opens *without reuse*
   ====================================================== */
  useEffect(() => {
    if (isOpen && !reuseTaskData) {
      resetForm();
    }
  }, [isOpen, reuseTaskData, resetForm]);

  /* ======================================================
     FETCH MEMBERS
     ====================================================== */
  useEffect(() => {
    if (!isOpen) return;

    axios.get(`${API_GET_ADMIN_MEMEBRS}?user_id=${userID}`)
      .then(res => setMembers(isEmpty(res.data?.data) ? [] : res.data.data))
      .catch(() => setMembers([]));
  }, [isOpen, userID]);

  /* ======================================================
     FETCH EVENTS WHEN DATE RANGE CHANGES
     ====================================================== */
  useEffect(() => {
    if (!isOpen || !startDate || !endDate) return;

    const fetchEvents = async () => {
      setLoading(true);
      try {
        const formattedStart = startDate.format("YYYY-MM-DD");
        const formattedEnd = endDate.format("YYYY-MM-DD");

        const eventsRes = await axios.get(
          `${API_GET_UPCOMING_ENQUIRIES}?user_id=${userID}&start_date=${formattedStart}&end_date=${formattedEnd}`
        );
        setEvents(isEmpty(eventsRes.data?.data) ? [] : eventsRes.data.data);
      } catch (err) {
        console.error("Fetch events error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [isOpen, startDate, endDate, userID]);

  /* ======================================================
     ⭐ HANDLERS
     ====================================================== */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Task name is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.event.trim()) newErrors.event = 'Event selection is required';
    if (!formData.assignedTo.length) newErrors.assignedTo = 'At least one assignee is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      toast.error("Please fill all mandatory fields.");
      return;
    }

    setIsSubmitting(true);

    const selectedMemberObjects =
      members.filter(member => formData.assignedTo.includes(member.user_id));

    const payload = {
      name: formData.name,
      description: formData.description,
      enquiry_id: formData.event,
      priority: formData.priority,
      user_id: userID,
      assign_users: selectedMemberObjects,
      due_date: formData.dueDate,
    };

    try {
      await axios.post(API_ADD_UPDATE_TASKS, payload);
      toast.success("Task created successfully!");

      resetForm();
      if (onCreateTask) onCreateTask();
      onClose();
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Task Is Not Created. Please Try with Other Event.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  // const getMinDate = () => new Date().toISOString().split('T')[0];
  // const priorityOptions = ['Low', 'Medium', 'High'];

  if (!isOpen) return null;

  return (
    <Box sx={{ position: 'fixed', right: 0, top: 0, height: '100%', width: '100%', maxWidth: 500, bgcolor: 'background.paper', boxShadow: 6, zIndex: 1300, overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h3">{reuseTaskData ? "Reuse Task" : "Create New Task"}</Typography>
        <IconButton onClick={handleClose} disabled={isSubmitting}>
          <CancelIcon />
        </IconButton>
      </Box>

      <Box component="form" sx={{ p: 2 }} onSubmit={handleSubmit}>
        <MuiTypographyMandatory label="Task Name" required sx={{p:0}}/>
        <TextField
          fullWidth
          margin="normal"
          value={formData.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
          error={!!errors.name}
          helperText={errors.name}
        />

        <MuiTypographyMandatory label="Description" required />
        <TextField
          fullWidth
          margin="normal"
          multiline
          rows={4}
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          error={!!errors.description}
          helperText={errors.description}
        />

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ display: 'flex', gap: 2, mt: 2, width: '100%' }}>
          <DesktopDatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            slotProps={{
              textField: {
                error: !!errors.startDate,
                helperText: errors.startDate,
                fullWidth: true,
                sx: { backgroundColor: VM_Colors.white },
              },
            }}
          />
          <DesktopDatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            slotProps={{
              textField: {
                error: !!errors.endDate,
                helperText: errors.endDate,
                fullWidth: true,
                sx: { backgroundColor: VM_Colors.white },
             
              },
            }}
          />
        </Box>
      </LocalizationProvider>


        <MuiTypographyMandatory label="Select Event" required />
        <FormControl fullWidth margin="normal" error={!!errors.event}>
          <Select
            value={formData.event}
            onChange={(e) => handleInputChange('event', e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>-- Select --</MenuItem>
            {events.map(ev => (
              <MenuItem key={ev.appointment_id} value={ev.appointment_id}>
                {ev.name} [
                {Array.isArray(ev.appointment_date)
                  ? ev.appointment_date.map(d => returnDate(d)).join(', ')
                  : returnDate(ev.appointment_date)}
                ]
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.event}</FormHelperText>
        </FormControl>

        <MuiTypographyMandatory label="Priority" required />
        <Select
          fullWidth
          margin="normal"
          value={formData.priority}
          onChange={(e) => handleInputChange('priority', e.target.value)}
        >
          <MenuItem value="Low">Low</MenuItem>
          <MenuItem value="Medium">Medium</MenuItem>
          <MenuItem value="High">High</MenuItem>
        </Select>

        <MuiTypographyMandatory label="Select Team Member" required />
        <FormControl fullWidth margin="normal" error={!!errors.assignedTo}>
          <Select
            value={formData.assignedTo[0] || ''}
            onChange={(e) => handleInputChange('assignedTo', [e.target.value])}
            displayEmpty
          >
            <MenuItem value="" disabled>-- Select --</MenuItem>
            {members.map(m => (
              <MenuItem key={m.user_id} value={m.user_id}>
                {m.name}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors.assignedTo}</FormHelperText>
        </FormControl>

        <MuiTypographyMandatory label="Due Date" required />
        <TextField
          type="date"
          fullWidth
          margin="normal"
          value={formData.dueDate}
          onChange={(e) => handleInputChange('dueDate', e.target.value)}
          error={!!errors.dueDate}
          helperText={errors.dueDate}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
        />

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={handleClose} variant="outlined" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {reuseTaskData ? "Create From Task" : "Create Task"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default TaskDrawer;
